import { useCallback, useMemo } from 'react';
import { useChatState } from '../store/useAppStore';

export const useChats = () => {
  const {
    allChats,
    selectedChats,
    searchQuery,
    filteredChats,
    setSelectedChats,
    toggleChatSelection,
    selectAllFilteredChats,
    deselectAllFilteredChats,
    clearSelection,
    setSearchQuery,
    refreshChats
  } = useChatState();

  // Verificar se todos os chats filtrados estão selecionados
  const areAllFilteredSelected = useMemo(() => {
    return filteredChats.length > 0 && 
           filteredChats.every(chat => selectedChats.includes(chat.id));
  }, [filteredChats, selectedChats]);

  // Contadores úteis
  const counts = useMemo(() => ({
    total: allChats.length,
    filtered: filteredChats.length,
    selected: selectedChats.length,
    selectedFromFiltered: filteredChats.filter(chat => selectedChats.includes(chat.id)).length
  }), [allChats, filteredChats, selectedChats]);

  // Handler para busca
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, [setSearchQuery]);

  // Handler para seleção individual
  const handleChatSelection = useCallback((chatId: string) => {
    toggleChatSelection(chatId);
  }, [toggleChatSelection]);

  // Handler para selecionar/deselecionar todos os filtrados
  const handleToggleAllFiltered = useCallback(() => {
    if (areAllFilteredSelected) {
      deselectAllFilteredChats();
    } else {
      selectAllFilteredChats();
    }
  }, [areAllFilteredSelected, selectAllFilteredChats, deselectAllFilteredChats]);

  // Handler para limpar seleção
  const handleClearSelection = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  // Handler para refresh dos chats
  const handleRefresh = useCallback(async () => {
    console.log('🔄 Atualizando lista de chats...');
    await refreshChats();
  }, [refreshChats]);

  // Função para obter nome do grupo por ID
  const getGroupName = useCallback((groupId: string): string => {
    const chat = allChats.find(chat => chat.id === groupId);
    return chat ? chat.subject : groupId;
  }, [allChats]);

  // Função para verificar se um chat está selecionado
  const isChatSelected = useCallback((chatId: string): boolean => {
    return selectedChats.includes(chatId);
  }, [selectedChats]);

  // Função para obter chats selecionados (objetos completos)
  const getSelectedChats = useCallback(() => {
    return allChats.filter(chat => selectedChats.includes(chat.id));
  }, [allChats, selectedChats]);

  // Função para selecionar chats por IDs
  const selectChatsByIds = useCallback((chatIds: string[]) => {
    setSelectedChats(chatIds);
  }, [setSelectedChats]);

  // Função para adicionar chats à seleção atual
  const addToSelection = useCallback((chatIds: string[]) => {
    const newSelection = [...new Set([...selectedChats, ...chatIds])];
    setSelectedChats(newSelection);
  }, [selectedChats, setSelectedChats]);

  // Função para remover chats da seleção atual
  const removeFromSelection = useCallback((chatIds: string[]) => {
    const newSelection = selectedChats.filter(id => !chatIds.includes(id));
    setSelectedChats(newSelection);
  }, [selectedChats, setSelectedChats]);

  // Estatísticas da busca
  const searchStats = useMemo(() => ({
    hasQuery: !!searchQuery,
    isFiltered: searchQuery.length > 0,
    matchCount: filteredChats.length,
    totalCount: allChats.length,
    percentage: allChats.length > 0 ? Math.round((filteredChats.length / allChats.length) * 100) : 0
  }), [searchQuery, filteredChats, allChats]);

  return {
    // Estado
    allChats,
    selectedChats,
    filteredChats,
    searchQuery,
    areAllFilteredSelected,
    counts,
    searchStats,

    // Handlers básicos
    handleSearch,
    handleChatSelection,
    handleToggleAllFiltered,
    handleClearSelection,
    handleRefresh,

    // Funções utilitárias
    getGroupName,
    isChatSelected,
    getSelectedChats,

    // Funções avançadas de seleção
    selectChatsByIds,
    addToSelection,
    removeFromSelection
  };
};